import { ApolloClient, InMemoryCache, HttpLink, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import {
  DATOCMS_API_URL,
  DATOCMS_API_TOKEN,
  DATOCMS_ENVIRONMENT,
  CMS_ENABLED,
} from '../../config/envConfig';

let datoCmsClient = null;

/**
 * Check if CMS integration is enabled
 * @returns {boolean}
 */
export const isCmsEnabled = () => {
  return CMS_ENABLED === 'true' || CMS_ENABLED === true;
};

/**
 * Get the DatoCMS Apollo client instance
 * Creates a singleton client for CMS queries
 * @returns {ApolloClient}
 */
export const getDatoCmsClient = () => {
  if (!isCmsEnabled()) {
    console.warn('CMS integration is disabled');
    return null;
  }

  if (datoCmsClient) {
    return datoCmsClient;
  }

  const apiUrl = process.env.REACT_APP_DATOCMS_API_URL || DATOCMS_API_URL;
  const apiToken = process.env.REACT_APP_DATOCMS_API_TOKEN || DATOCMS_API_TOKEN;
  const environment =
    process.env.REACT_APP_DATOCMS_ENVIRONMENT || DATOCMS_ENVIRONMENT || 'main';

  if (!apiUrl || !apiToken) {
    console.error('DatoCMS API URL or Token is not configured');
    return null;
  }

  const httpLink = new HttpLink({
    uri: apiUrl,
    headers: {
      Authorization: `Bearer ${apiToken}`,
      // Include environment header if not using main
      ...(environment && environment !== 'main'
        ? { 'X-Environment': environment }
        : {}),
    },
  });

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        console.error(
          `[DatoCMS GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`
        );
      });
    }

    if (networkError) {
      console.error(`[DatoCMS Network error]: ${networkError}`);
    }
  });

  datoCmsClient = new ApolloClient({
    link: from([errorLink, httpLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-first',
      },
      query: {
        fetchPolicy: 'cache-first',
      },
    },
  });

  return datoCmsClient;
};

/**
 * Reset the DatoCMS client (useful for testing or re-initialization)
 */
export const resetDatoCmsClient = () => {
  if (datoCmsClient) {
    datoCmsClient.clearStore();
    datoCmsClient = null;
  }
};

export default getDatoCmsClient;
